import apiService from './api';

export interface Review {
  id: number;
  userId: number;
  productId: number;
  rating: number;
  comment: string;
  title?: string;
  isVerified: boolean;
  isModerated: boolean;
  helpfulVotes: number;
  totalVotes: number;
  helpfulPercentage: number;
  images?: string[];
  createdAt: Date;
  updatedAt: Date;
  user?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateReviewData {
  rating: number;
  comment: string;
  title?: string;
  images?: string[];
}

export interface UpdateReviewData {
  rating?: number;
  comment?: string;
  title?: string;
  images?: string[];
}

export interface ReviewFilterParams {
  rating?: number;
  isVerified?: boolean;
  sortBy?: 'rating' | 'createdAt' | 'helpfulVotes';
  sortOrder?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

export interface ReviewResponse {
  reviews: Review[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProductRatingSummary {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    '1': number;
    '2': number;
    '3': number;
    '4': number;
    '5': number;
  };
  verifiedReviews: number;
}

class ReviewService {
  async createReview(productId: number, data: CreateReviewData): Promise<Review> {
    try {
      // Check if user is authenticated before making the request
      // Only use authToken for user operations, not dev_token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please login to submit a review.');
      }
      
      // Check if token might be expired and try to refresh it proactively
      try {
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const exp = payload.exp;
          if (exp) {
            const expirationDate = new Date(exp * 1000);
            const now = new Date();
            const timeUntilExpiry = expirationDate.getTime() - now.getTime();
            
            if (expirationDate < now) {
              // Try to refresh the token before making the request
              try {
                const refreshResponse = await apiService.post<{ success: boolean; token: string }>('/auth/refresh', {});
                if (refreshResponse.token) {
                  localStorage.setItem('authToken', refreshResponse.token);
                  // Token refreshed, continue with the request
                } else {
                  throw new Error('Token refresh failed. Please login again.');
                }
              } catch (refreshError) {
                throw new Error('Your session has expired. Please login again.');
              }
            } else if (timeUntilExpiry < 5 * 60 * 1000) {
              // Token expires in less than 5 minutes - refresh proactively
              try {
                const refreshResponse = await apiService.post<{ success: boolean; token: string }>('/auth/refresh', {});
                if (refreshResponse.token) {
                  localStorage.setItem('authToken', refreshResponse.token);
                }
              } catch (refreshError) {
                // If proactive refresh fails, continue with existing token
              }
            }
          }
        }
      } catch (tokenError: any) {
        // If token parsing fails, continue anyway - backend will validate
      }
      
      const response = await apiService.post<Review>(`/reviews/products/${productId}`, data);
      return response;
    } catch (error: any) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create review';
      const status = error?.status || 0;
      
      // Provide more specific error messages
      if (status === 401 || errorMessage.includes('401') || errorMessage.includes('Unauthorized')) {
        // Check if token exists
        const token = localStorage.getItem('authToken') || localStorage.getItem('dev_token');
        if (!token) {
          throw new Error('Please login to submit a review.');
        } else if (errorMessage.includes('expired') || errorMessage.includes('expire')) {
          throw new Error('Your session has expired. Please login again.');
        } else {
          throw new Error('Authentication failed. Please try logging out and logging back in.');
        }
      }
      
      throw new Error(errorMessage);
    }
  }

  async updateReview(reviewId: number, data: UpdateReviewData): Promise<Review> {
    try {
      const response = await apiService.put<Review>(`/reviews/${reviewId}`, data);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to update review');
    }
  }

  async deleteReview(reviewId: number): Promise<void> {
    try {
      await apiService.delete(`/reviews/${reviewId}`);
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to delete review');
    }
  }

  async getProductReviews(productId: number, filters?: ReviewFilterParams): Promise<ReviewResponse> {
    try {
      const params = new URLSearchParams();
      if (filters?.rating) params.append('rating', filters.rating.toString());
      if (filters?.isVerified !== undefined) params.append('isVerified', filters.isVerified.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters?.page) params.append('page', filters.page.toString());
      if (filters?.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const url = queryString 
        ? `/reviews/products/${productId}?${queryString}`
        : `/reviews/products/${productId}`;
      const response = await apiService.get<ReviewResponse>(url);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch reviews');
    }
  }

  async getProductRatingSummary(productId: number): Promise<ProductRatingSummary> {
    try {
      const response = await apiService.get<ProductRatingSummary>(`/reviews/products/${productId}/rating`);
      return response;
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to fetch rating summary');
    }
  }

  async voteReview(reviewId: number, isHelpful: boolean): Promise<void> {
    try {
      await apiService.post(`/reviews/${reviewId}/vote`, { isHelpful });
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to vote on review');
    }
  }
}

export const reviewService = new ReviewService();

